import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { SceneClock, sceneTimeBus } from "../../common/scene_clock.es6";

// Courses produced before playback metadata existed do not carry `seg.playback`.
// Keep the first catalogue rule available here so those saved courses gain the
// corrected clap loop after an editor refresh as well.
const LEGACY_PLAYBACK_BY_MOVEMENT = {
  clap: { mode: "loop-range", loopStart: 2, loopEnd: 3 },
  wave: { mode: "loop-range", loopStart: 3, loopEnd: 4 },
  listen: { mode: "loop-range", loopStart: 3, loopEnd: 4 },
  useDevice: { mode: "loop-range", loopStart: 1, loopEnd: 2 },
  useDeviceSeated: { mode: "loop-range", loopStart: 1, loopEnd: 2 },
  count: { mode: "once-hold" },
};

/**
 * Plays a character "story" scene deterministically, DRIVEN BY THE TIMELINE.
 *
 * state = { characterId, sceneDurationSec, segments: [{
 *   movement, view, frames:[url...], fps, fromXPct, toXPct, yPct, heightPct,
 *   start, end, mouthSync, audioUrl, audioDuration
 * }] }
 *
 * TIME SOURCE (in priority) — the character NEVER runs on its own free clock while
 * the timeline is stopped, and it NEVER plays its own audio (audio lives on the
 * timeline soundList track, so the plugin playing it too would double up):
 *
 * 1. Visor: live playback follows the scene's committed frame (box-relative via
 *    `timeOriginSec`); the recorder uses `visorCurrentTime`. Fully deterministic.
 * 2. Editor playhead — the editor emits `aidea:char-time` ticks (box-relative
 *    seconds) from its timeline. Paused → no ticks → the character freezes at the
 *    last playhead position; scrub/play → it follows exactly.
 * 3. Live visor play (`props.visor` true) — wall-clock from mount, PAUSED when
 *    `visorPlaying === false`. Fallback only; VisorBox passes a time origin.
 * 4. Otherwise (editor, before any tick) — frozen at t=0. No wall-clock in the
 *    editor, so a stopped timeline shows a still character.
 *
 * Character frames are transparent PNGs (alpha), rendered as-is over the scene.
 */
export default function CharacterPlayback({
  state,
  boxId,
  visor,
  visorCurrentTime,
  visorPlaying,
  recorderMode = false,
  timeOriginSec = null,
  sceneId = null,
}) {
  const segments = (state && state.segments) || [];
  const duration = (state && state.sceneDurationSec) || 0;

  // Live Visor: this scene's committed frame; the recorder: the time it is given.
  const live = visor === true && !recorderMode && timeOriginSec != null && sceneId != null;
  const lastFrame = useRef(null);
  const committedFrame = useSyncExternalStore(sceneTimeBus.subscribeFrame, () => {
    if (live && sceneTimeBus.frameSceneId === sceneId) lastFrame.current = sceneTimeBus.frame;
    return lastFrame.current;
  });
  const recorder = recorderMode && typeof visorCurrentTime === "number";
  const liveDriven = live && committedFrame != null;

  // Editor timeline playhead (box-relative seconds), pushed via window events.
  const [editorT, setEditorT] = useState(null);
  useEffect(() => {
    if (recorder || liveDriven || !boxId) return;
    const onTick = (e) => {
      const d = e && e.detail;
      if (!d || d.layerId !== boxId) return;
      setEditorT(d.inRange === false ? null : typeof d.t === "number" ? d.t : 0);
    };
    window.addEventListener("aidea:char-time", onTick);
    return () => window.removeEventListener("aidea:char-time", onTick);
  }, [recorder, liveDriven, boxId]);

  const editorDriven = editorT != null;
  // Wall-clock is ONLY for live end-user visor playback (never the editor).
  const useWallClock = !recorder && !liveDriven && !editorDriven && visor === true;
  const isPaused = visorPlaying === false;

  const [liveT, setLiveT] = useState(0);
  const clockRef = useRef(null);
  if (!clockRef.current) clockRef.current = new SceneClock();
  useEffect(() => {
    if (!useWallClock || isPaused) return;
    let raf;
    const clock = clockRef.current;
    clock.start();
    const loop = () => {
      const elapsed = clock.positionSec();
      const el = duration > 0 ? elapsed % duration : elapsed;
      setLiveT(el);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      clock.pause();
      cancelAnimationFrame(raf);
    };
  }, [useWallClock, isPaused, duration]);

  // "Rest" = editor at rest before any play/scrub: no clock drives the character.
  // At t=0 the first beat is usually an off-screen entrance, so a static preview
  // would show nothing. Instead show a representative on-screen pose (a talking
  // beat if any, else a middle beat) so the author always sees the character.
  const restMode = !recorder && !liveDriven && !editorDriven && !useWallClock;

  const time = recorder
    ? visorCurrentTime
    : liveDriven
      ? Math.max(0, committedFrame / 30 - timeOriginSec)
      : editorDriven
        ? editorT
        : useWallClock
          ? liveT
          : 0;

  // Active segment.
  let seg = null;
  let local;
  if (restMode && segments.length) {
    const poster =
      segments.find((s) => s.mouthSync) || segments[Math.floor(segments.length / 2)] || segments[0];
    seg = poster;
    local = Math.max(0, (poster.end - poster.start) / 2); // mid-pose
  } else {
    for (const s of segments) {
      if (time >= s.start && time < s.end) {
        seg = s;
        break;
      }
    }
    if (!seg && segments.length) {
      // Before first / after last: clamp to nearest so a frame is always drawn.
      seg = time < segments[0].start ? segments[0] : segments[segments.length - 1];
    }
    if (seg) local = Math.max(0, time - seg.start);
  }
  if (!seg) return <div style={{ width: "100%", height: "100%" }} />;

  const segDur = Math.max(0.001, seg.end - seg.start);
  const prog = Math.min(1, local / segDur);
  // At rest keep the character on-screen (never a half-off-screen entrance pose).
  let x = seg.fromXPct + (seg.toXPct - seg.fromXPct) * prog;
  if (restMode) x = Math.min(90, Math.max(10, x));

  const frames = seg.frames || [];
  let idx = 0;
  if (frames.length) {
    const fps = seg.fps || 4;
    // Old saved courses may predate the explicit `mouthSync` flag. A voiced
    // talk segment with multiple pose frames is still a safe mouth-sync signal.
    const mouthSync =
      seg.mouthSync === true ||
      (/^talk/i.test(seg.movement || "") && !!seg.audioUrl && frames.length > 1);
    if (mouthSync) {
      // Talk: the mouth must ALWAYS be moving for the whole spoken window — never a
      // frozen frame while audio plays. Cycle the mouth frames at a dedicated (faster)
      // rate; after speech ends hold frame 0 (closed).
      const MOUTH_FPS = 8;
      const speakUntil = seg.audioDuration && seg.audioDuration > 0 ? seg.audioDuration : segDur;
      const speaking = local < speakUntil;
      idx = speaking ? Math.floor(local * MOUTH_FPS) % frames.length : 0;
    } else {
      const elapsedFrames = Math.floor(local * fps);
      const playback = seg.playback ||
        LEGACY_PLAYBACK_BY_MOVEMENT[seg.movement] || { mode: "loop-all" };
      if (playback.mode === "once-hold") {
        idx = Math.min(elapsedFrames, frames.length - 1);
      } else if (playback.mode === "loop-range") {
        const loopStart = Math.max(0, Math.min(playback.loopStart || 0, frames.length - 1));
        const loopEnd = Math.max(loopStart, Math.min(playback.loopEnd || 0, frames.length - 1));
        idx =
          elapsedFrames < loopStart
            ? elapsedFrames
            : loopStart + ((elapsedFrames - loopStart) % (loopEnd - loopStart + 1));
      } else {
        idx = elapsedFrames % frames.length;
      }
    }
  }
  const src = frames[idx] || frames[0];
  // Locomotion assets are authored facing screen-right.  Mirror the rendered
  // sprite—not its position—when the timeline moves it right-to-left, so the
  // same generated walk/run cycle can travel naturally in both directions.
  const isMovingLeft =
    (seg.movement === "walk" || seg.movement === "run") &&
    Number(seg.toXPct) < Number(seg.fromXPct);

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      {src && (
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            left: x + "%",
            top: (seg.yPct != null ? seg.yPct : 90) + "%",
            height: (seg.heightPct != null ? seg.heightPct : 82) + "%",
            transform: `translate(-50%, -100%)${isMovingLeft ? " scaleX(-1)" : ""}`, // anchor: horizontal center + feet
            transformOrigin: "center bottom",
            // Character frames are transparent PNGs (alpha) — render as-is, no blend
            // keying. (multiply tinted the character over colored backgrounds.)
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      )}
    </div>
  );
}
